import React, { FC, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import {
    Box,
    Chip,
    ChipMenu,
    ConfirmationDialog,
    GridColDef,
    GridSortModel,
    IconButton,
    Icons,
    LoadingButton,
    SectionMessage,
    Stack,
    Tooltip,
    TSLLink,
} from '@thirdspacelearning/library';

import { SlidesPreviewDialog, ViewType } from '../../../learning-content';
import { ProgrammeStatusType } from '../../types';

import {
    Country,
    GBLearningObjective,
    LearningObjective,
    USLearningObjective,
} from '@types';

import { loActiveChipOptions, yearGroupSortComparator } from '../../utils';

import {
    deleteLearningObjectives,
    editLearningObjective,
    getLearningObjective,
} from '@api';
import { CurriculumQueryKeys } from '@services';
import { CMSDataGrid } from '@shared';

type Props = {
    los: LearningObjective[];
    programmeId: number;
    programmeStatus: ProgrammeStatusType | null;
    country: Country;
};

const ProgrammeTable: FC<Props> = ({
    los,
    programmeStatus,
    programmeId,
    country,
}) => {
    const queryClient = useQueryClient();

    const [showSlidesPreview, setShowSlidesPreview] = useState(false);
    const [loId, setLoId] = useState<number | undefined>();

    const [showDeleteLO, setShowDeleteLO] = useState(false);
    const [deleteLoId, setDeleteLOId] = useState<number | undefined>();

    const { data: learningObjective, isFetching } = useQuery<LearningObjective>(
        {
            queryKey: [`${CurriculumQueryKeys.LO}-${loId}`],
            queryFn: () => getLearningObjective(loId),
            enabled: !!loId,
            retry: 1,
            refetchOnWindowFocus: false,
        },
    );

    const { mutate: deleteLOs, isPending: isDeletingLOs } = useMutation({
        mutationFn: () => deleteLearningObjectives(programmeId, [deleteLoId!]),
        onSuccess: () => {
            enqueueSnackbar('Successfully removed learning objective.', {
                key: 'remove-los-success',
                testId: 'remove-los-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            setShowDeleteLO(false);
            setDeleteLOId(undefined);

            queryClient.invalidateQueries({
                queryKey: [`${CurriculumQueryKeys.PROGRAMME}-${programmeId}`],
            });
        },
        onError: (error: Error) => {
            enqueueSnackbar('Please refresh or try again.', {
                title: error.message,
                key: 'remove-los-error',
                testId: 'remove-los-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const { mutate: editLO, isPending: isEditingLO } = useMutation({
        mutationFn: (data: { id: number; data: any }) =>
            editLearningObjective(data.id, data.data),
        onSuccess: () => {
            enqueueSnackbar('Successfully edited learning objective.', {
                key: 'edit-los-success',
                testId: 'edit-los-success',
                variant: 'curriculumSnackbar',
                severity: 'success',
            });

            queryClient.invalidateQueries({
                queryKey: [`${CurriculumQueryKeys.PROGRAMME}-${programmeId}`],
            });
        },
        onError: (error: Error) => {
            enqueueSnackbar('Please refresh or try again.', {
                title: error.message,
                key: 'edit-los-error',
                testId: 'edit-los-error',
                variant: 'curriculumSnackbar',
                severity: 'error',
            });
        },
    });

    const onEditLO = (id: number, active: string | number) => {
        editLO({
            id: id,
            data: { active: active === 'active' },
        });
    };

    const onShowSlides = (learningObjectiveId: number) => {
        setShowSlidesPreview(true);
        setLoId(learningObjectiveId);
    };

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'programme_position',
            sort: 'asc',
        },
    ]);

    const GBColumns: GridColDef<GBLearningObjective>[] = [
        {
            field: 'yearGroup',
            type: 'string',
            headerName: 'Year group',
            sortingOrder: ['asc', 'desc'],
            sortComparator: yearGroupSortComparator,
            display: 'flex',
            renderCell: ({ row }) =>
                row.year_id ? (
                    <Chip
                        label={`Year ${row.year_id}`}
                        variant="outlined"
                        size="small"
                    />
                ) : (
                    '-'
                ),
            sortable: true,
            minWidth: 120,
            flex: 0.5,
        },
    ];

    const USColumns: GridColDef<USLearningObjective>[] = [
        {
            field: 'yearGroup',
            type: 'string',
            headerName: 'Grade',
            sortingOrder: ['asc', 'desc'],
            sortComparator: yearGroupSortComparator,
            display: 'flex',
            renderCell: ({ row }) =>
                row.year_id ? (
                    <Chip
                        label={`Grade ${row.year_id}`}
                        variant="outlined"
                        size="small"
                    />
                ) : (
                    '-'
                ),
            sortable: true,
            minWidth: 120,
            flex: 0.5,
        },
        {
            field: 'standardCode',
            type: 'string',
            headerName: 'Standard Code',
            display: 'flex',
            renderCell: ({ row }) =>
                'standard_codes' in row && (
                    <Stack direction="row" flexWrap="wrap">
                        {row.standard_codes.map((standardCode, idx) => (
                            <Chip
                                key={idx}
                                label={standardCode}
                                variant="outlined"
                                size="small"
                                sx={{ mb: '2px', mr: 1 }}
                            />
                        ))}
                    </Stack>
                ),
            sortable: false,
            minWidth: 120,
            flex: 0.5,
        },
    ];

    const columns: GridColDef<LearningObjective>[] = [
        {
            field: 'programme_position',
            type: 'string',
            headerName: 'Order',
            headerAlign: 'left',
            align: 'left',
            sortingOrder: ['asc', 'desc'],
            sortable: true,
            width: 100,
        },
        {
            field: 'title',
            type: 'string',
            headerName: 'Learning objective title',
            display: 'flex',
            renderCell: ({ row }) => (
                <Box
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    <TSLLink
                        to={`/learning-content/${row.id}`}
                        variant="body2"
                        data-testid="programme-link"
                    >
                        {row.ai && (
                            <Chip
                                label={'AI'}
                                color="info"
                                size="small"
                                sx={{ mb: '2px', mr: 1 }}
                            />
                        )}
                        {row.title}
                    </TSLLink>
                </Box>
            ),
            sortable: true,
            minWidth: 250,
            flex: 1.5,
        },
        {
            field: 'id',
            type: 'string',
            headerName: 'ID',
            sortable: true,
            minWidth: 100,
            flex: 0.5,
        },
        {
            field: 'active',
            type: 'string',
            headerName: 'Status',
            display: 'flex',
            renderCell: ({ row }) => (
                <ChipMenu
                    items={loActiveChipOptions}
                    onChange={(data: string | number) => onEditLO(row.id, data)}
                    placeholder={'No status'}
                    value={row.active ? 'active' : 'inactive'}
                />
            ),
            sortable: true,
            minWidth: 110,
            flex: 0.5,
        },
        ...(country === Country.GB
            ? (GBColumns as GridColDef<LearningObjective>[])
            : (USColumns as GridColDef<LearningObjective>[])),
        {
            field: 'slides_counter',
            type: 'string',
            headerName: 'Slide count',
            valueGetter: (value, row) => `${row.slides_counter} slides`,
            sortable: true,
            minWidth: 120,
            flex: 0.5,
        },
        {
            field: 'preview',
            headerName: '',
            display: 'flex',
            renderCell: ({ row }) => (
                <LoadingButton
                    variant="outlined"
                    color="primary"
                    onClick={() => onShowSlides(row.id)}
                    loading={row.id === loId && isFetching}
                >
                    Preview
                </LoadingButton>
            ),
            sortable: false,
            width: 114,
        },
        {
            field: 'remove',
            headerName: '',
            renderCell: ({ row }) => (
                <Tooltip
                    title={
                        programmeStatus === ProgrammeStatusType.LIVE
                            ? "You can't remove an LO from a live programme"
                            : 'Remove LO'
                    }
                    disableInteractive
                    arrow
                >
                    <Box>
                        <IconButton
                            color="error"
                            onClick={() => {
                                setShowDeleteLO(true);
                                setDeleteLOId(row.id);
                            }}
                            data-testid="remove-lo-button"
                            disabled={
                                programmeStatus === ProgrammeStatusType.LIVE
                            }
                        >
                            <Icons.Close />
                        </IconButton>
                    </Box>
                </Tooltip>
            ),
            sortable: false,
            minWidth: 60,
            maxWidth: 60,
        },
    ];

    return (
        <>
            <CMSDataGrid
                columns={columns}
                rows={los}
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                disableColumnMenu
                disableColumnSelector
                disableColumnFilter
                disableDensitySelector
                disableRowSelectionOnClick
                disableColumnResize
                hideFooter
                loading={isEditingLO}
                slotProps={{
                    loadingOverlay: {
                        variant: 'linear-progress',
                        noRowsVariant: 'skeleton',
                    },
                }}
            />

            {showSlidesPreview && learningObjective && (
                <SlidesPreviewDialog
                    open={showSlidesPreview}
                    onClose={() => {
                        setShowSlidesPreview(false);
                        setLoId(undefined);
                    }}
                    startingSlide={0}
                    initialViewType={ViewType.VIEW}
                    learningObjective={learningObjective}
                />
            )}

            {showDeleteLO && (
                <ConfirmationDialog
                    open={showDeleteLO}
                    title={'Remove objective from programme?'}
                    confirmLabel="Yes"
                    loadingActionButton={isDeletingLOs}
                    message={
                        <SectionMessage
                            type="info"
                            title="This will not delete the learning objective from the Curriculum CMS"
                        />
                    }
                    onConfirm={() => deleteLOs()}
                    onClose={() => {
                        setShowDeleteLO(false);
                        setDeleteLOId(undefined);
                    }}
                    testId="delete-lo-dialog"
                />
            )}
        </>
    );
};

export default ProgrammeTable;
